import { Link } from 'react-router-dom';
import { ArrowRight, Users, Target, Award, Heart } from 'lucide-react';
import { PageSEO } from '@/components/SEO';

const verdier = [
  {
    icon: Target,
    title: 'Resultatorientert',
    description: 'Vi måler suksess i faktiske resultater for kundene våre, ikke i tomme løfter.',
  },
  {
    icon: Users,
    title: 'Samarbeid',
    description: 'Vi jobber tett med kundene våre og ser på oss selv som en del av deres team.',
  },
  {
    icon: Award,
    title: 'Kvalitet',
    description: 'Vi leverer alltid arbeid vi selv er stolte av, og setter standarden høyt.',
  },
  {
    icon: Heart,
    title: 'Engasjement',
    description: 'Vi brenner for det vi gjør og investerer genuint i kundenes suksess.',
  },
];

export default function OmOss() {
  return (
    <div>
      <PageSEO
        title="Om oss – Side Kraft Digitalt Markedsføringsbyrå"
        path="/om-oss"
        description="Lær om Side Kraft – ditt digitale byrå for vekst i hele Norge. Vi leverer resultatorienterte nettsider, SEO og digital strategi for ambisiøse bedrifter."
      />
      {/* Hero Section */}
      <section className="pt-16 md:pt-20 pb-16 md:pb-20 bg-muted" data-testid="om-oss-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <p className="font-manrope text-xs font-medium tracking-[0.3em] uppercase text-accent mb-4">
              Om oss
            </p>
            <h1 className="font-syne font-bold text-5xl md:text-6xl text-primary tracking-tight leading-[0.95]">
              Vi er et team som brenner for digital vekst
            </h1>
            <p className="font-manrope text-lg text-muted-foreground mt-8 leading-relaxed">
              Side Kraft ble grunnlagt med én visjon: å hjelpe norske bedrifter å lykkes digitalt. Vi kombinerer strategi, kreativitet og teknisk ekspertise for å skape løsninger som gir resultater.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 md:py-32" data-testid="story-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative group rounded-3xl overflow-hidden shadow-2xl ring-1 ring-primary/5">
              <div className="absolute -top-4 -left-4 w-full h-full border-2 border-accent/20 rounded-3xl" />
              <img
                src="https://images.unsplash.com/photo-1554465861-79f44edf8020?auto=format&fit=crop&q=80"
                alt="Vårt kontor – Side Kraft-teamet i arbeid"
                className="relative z-10 w-full h-[500px] object-cover transition-transform duration-700 group-hover:scale-[1.03]"
              />
            </div>

            <div>
              <p className="font-manrope text-xs font-medium tracking-[0.3em] uppercase text-accent mb-4">
                Vår historie
              </p>
              <h2 className="font-syne font-bold text-4xl md:text-5xl text-primary tracking-tight mb-8">
                Fra idé til resultat
              </h2>
              <div className="space-y-6 font-manrope text-muted-foreground leading-relaxed">
                <p>
                  Side Kraft ble startet med en enkel ambisjon: å gjøre digital markedsføring tilgjengelig og resultatdrevet for norske bedrifter i alle størrelser.
                </p>
                <p>
                  Vi så at mange bedrifter slet med å få verdi ut av sine digitale investeringer. Byråer lovet mye, men leverte ofte lite. Vi bestemte oss for å gjøre det annerledes.
                </p>
                <p>
                  I dag hjelper vi bedrifter over hele Norge med alt fra nettsider og SEO til digital annonsering og merkevarebygging. Vår tilnærming er alltid den samme: vi lytter, vi forstår, og vi leverer.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 md:py-32 bg-primary text-primary-foreground" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mb-16">
            <p className="font-manrope text-xs font-medium tracking-[0.3em] uppercase text-accent mb-4">
              Våre verdier
            </p>
            <h2 className="font-syne font-bold text-4xl md:text-5xl tracking-tight">
              Det vi står for
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {verdier.map((verdi, index) => (
              <div
                key={index}
                className="p-8 border border-primary-foreground/10 rounded-2xl hover:border-accent/50 transition-all duration-500 hover:shadow-lg group"
                data-testid={`value-card-${index}`}
              >
                <verdi.icon className="w-10 h-10 text-accent mb-6" />
                <h3 className="font-syne font-bold text-xl mb-3">
                  {verdi.title}
                </h3>
                <p className="font-manrope text-sm text-primary-foreground/70 leading-relaxed">
                  {verdi.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-muted" data-testid="om-oss-cta">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-syne font-bold text-4xl md:text-5xl text-primary tracking-tight mb-6">
            Klar for å samarbeide?
          </h2>
          <p className="font-manrope text-muted-foreground max-w-xl mx-auto mb-10">
            Ta kontakt med oss for en uforpliktende samtale om hvordan vi kan hjelpe din bedrift å vokse digitalt.
          </p>
          <Link
            to="/kontakt"
            className="inline-flex items-center gap-2 font-syne font-bold text-sm uppercase tracking-wide px-8 py-4 bg-primary text-primary-foreground hover:bg-accent transition-colors duration-300 group"
            data-testid="om-oss-cta-button"
          >
            Ta kontakt
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </div>
      </section>
    </div>
  );
}
